import React, { useState, useEffect } from "react";
import { 
  Phone, MapPin, Plus, History, Edit2, Save, X, 
  User, Mail, CheckCircle, AlertCircle, Loader2 
} from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";

// --- TYPES ---
interface Lead {
  id: number;
  clientName: string;
  phone: string;
  email: string;
  location: string;
  requirement: string;
  status: string;
  source: string;
  createdAt: string;
  remarks: string; // Latest remark
  callLogs: CallLog[];
}

interface CallLog {
  id: number;
  status: string;
  remark: string;
  callTime: string;
}

const LeadManager = () => {
  const { token } = useAdminAuth();
  
  // --- STATES ---
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [expandedLeadId, setExpandedLeadId] = useState<number | null>(null);

  // Toast Notification State
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Forms Data
  const [formData, setFormData] = useState({
    clientName: "", phone: "", email: "", location: "", requirement: "", 
    status: "NEW", remark: "" // Initial Status & Remark
  });

  const [logData, setLogData] = useState({ status: "DNP", remark: "" });
  const [editingReqId, setEditingReqId] = useState<number | null>(null);
  const [tempReq, setTempReq] = useState("");

  // --- HELPER: SHOW TOAST ---
  const showToast = (message: string, type: "success" | "error") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ ...toast, show: false }), 3000); // 3 sec baad gayab
  };

  // --- API CALLS ---
  const fetchLeads = async () => {
    setLoading(true);
    try {
      // const res = await fetch("http://localhost:1001/api/ops/leads", {
      const res = await fetch("https://luxuriyahomes.com/api/ops/leads", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setLeads(data);
    } catch (err) {
      showToast("Failed to load leads.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, []);

  // 1. ADD NEW LEAD (With Initial Status)
  const handleAddLead = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // const res = await fetch("http://localhost:1001/api/ops/lead/add", {
      const res = await fetch("https://luxuriyahomes.com/api/ops/lead/add", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        showToast("New Lead Added Successfully!", "success");
        setShowForm(false);
        setFormData({ clientName: "", phone: "", email: "", location: "", requirement: "", status: "NEW", remark: "" });
        fetchLeads();
      } else {
        showToast("Error adding lead.", "error");
      }
    } catch (error) {
      showToast("Server Connection Failed", "error");
    }
  };

  // 2. LOG CALL (Update Status)
  const handleLogSubmit = async (leadId: number) => {
    if (!logData.remark) return showToast("Please enter a remark!", "error");
    
    try {
      // const res = await fetch(`http://localhost:1001/api/ops/lead/${leadId}/call-log`, {
      const res = await fetch(`https://luxuriyahomes.com/api/ops/lead/${leadId}/call-log`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(logData)
      });

      if (res.ok) {
        showToast("Call Logged & Status Updated!", "success");
        setLogData({ status: "DNP", remark: "" });
        fetchLeads();
      }
    } catch (err) {
      showToast("Failed to update status", "error");
    }
  };

  // 3. UPDATE REQUIREMENT
  const handleReqUpdate = async (leadId: number) => {
    try {
      // const res = await fetch(`http://localhost:1001/api/ops/lead/${leadId}/requirement`, {
      const res = await fetch(`https://luxuriyahomes.com/api/ops/lead/${leadId}/requirement`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ requirement: tempReq })
      });
      
      if (res.ok) {
        showToast("Requirement Updated!", "success");
        setEditingReqId(null);
        fetchLeads();
      }
    } catch (err) {
      showToast("Update Failed", "error");
    }
  };

  return (
    <div className="p-4 md:p-8 text-white max-w-7xl mx-auto relative min-h-screen">
      
      {/* --- CUSTOM TOAST NOTIFICATION --- */}
      {toast.show && (
        <div className={`fixed top-5 left-1/2 transform -translate-x-1/2 z-50 flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl border animate-in slide-in-from-top-5 duration-300 ${
          toast.type === "success" 
            ? "bg-green-900/90 border-green-700 text-green-100" 
            : "bg-red-900/90 border-red-700 text-red-100"
        }`}>
          {toast.type === "success" ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          <span className="font-medium text-sm">{toast.message}</span>
        </div>
      )}

      {/* --- HEADER --- */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div>
           <h2 className="text-3xl font-light text-amber-500 tracking-wide">Lead Management</h2>
           <p className="text-zinc-500 text-sm mt-1">Track website enquiries & manual leads</p>
        </div>
        
        <button 
            onClick={() => setShowForm(!showForm)} 
            className={`px-6 py-3 rounded-lg flex gap-2 items-center font-bold transition-all shadow-lg ${
              showForm 
                ? "bg-zinc-800 text-zinc-400 hover:bg-zinc-700" 
                : "bg-gradient-to-r from-amber-600 to-amber-500 text-black hover:scale-105"
            }`}
        >
          {showForm ? <X size={20}/> : <Plus size={20} />} 
          {showForm ? "Cancel Entry" : "Add New Lead"}
        </button>
      </div>

      {/* --- ADD NEW LEAD FORM --- */}
      {showForm && (
        <div className="bg-[#161616] p-6 md:p-8 rounded-2xl border border-zinc-800 mb-10 shadow-2xl animate-in fade-in zoom-in-95 duration-300">
          <h3 className="text-amber-500 mb-6 text-xs uppercase tracking-[0.2em] font-bold border-b border-zinc-800 pb-3">
            New Lead Registration
          </h3>
          <form onSubmit={handleAddLead} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Basic Info */}
            <div className="space-y-1">
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Client Name *</label>
                <div className="relative group">
                    <User className="absolute left-3 top-3 text-zinc-600 group-focus-within:text-amber-500 transition-colors" size={18}/>
                    <input className="w-full bg-[#0c0c0c] border border-zinc-800 p-3 pl-10 rounded-lg text-white focus:border-amber-500 outline-none transition-all" 
                        placeholder="Rahul Sharma"
                        value={formData.clientName}
                        onChange={e => setFormData({...formData, clientName: e.target.value})} required 
                    />
                </div>
            </div>

            <div className="space-y-1">
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Phone Number *</label>
                <div className="relative group">
                    <Phone className="absolute left-3 top-3 text-zinc-600 group-focus-within:text-amber-500 transition-colors" size={18}/>
                    <input className="w-full bg-[#0c0c0c] border border-zinc-800 p-3 pl-10 rounded-lg text-white focus:border-amber-500 outline-none transition-all" 
                        placeholder="9876543210"
                        value={formData.phone}
                        onChange={e => setFormData({...formData, phone: e.target.value})} required 
                    />
                </div>
            </div>

            <div className="space-y-1">
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Email (Optional)</label>
                <div className="relative group">
                    <Mail className="absolute left-3 top-3 text-zinc-600 group-focus-within:text-amber-500 transition-colors" size={18}/>
                    <input className="w-full bg-[#0c0c0c] border border-zinc-800 p-3 pl-10 rounded-lg text-white focus:border-amber-500 outline-none transition-all" 
                        placeholder="client@gmail.com"
                        value={formData.email}
                        onChange={e => setFormData({...formData, email: e.target.value})} 
                    />
                </div>
            </div>

            <div className="space-y-1">
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Location</label>
                <div className="relative group">
                    <MapPin className="absolute left-3 top-3 text-zinc-600 group-focus-within:text-amber-500 transition-colors" size={18}/>
                    <input className="w-full bg-[#0c0c0c] border border-zinc-800 p-3 pl-10 rounded-lg text-white focus:border-amber-500 outline-none transition-all" 
                        placeholder="Delhi, Dwarka Sector 12"
                        value={formData.location}
                        onChange={e => setFormData({...formData, location: e.target.value})} 
                    />
                </div>
            </div>

            {/* Requirement */}
            <div className="md:col-span-2 space-y-1">
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Requirement / Vision</label>
                <textarea className="w-full bg-[#0c0c0c] border border-zinc-800 p-3 rounded-lg text-white focus:border-amber-500 outline-none h-20 transition-all resize-none" 
                    placeholder="Describe what the client needs..."
                    value={formData.requirement}
                    onChange={e => setFormData({...formData, requirement: e.target.value})} 
                />
            </div>

            {/* INITIAL STATUS & REMARK (Highlighted) */}
            <div className="md:col-span-2 bg-amber-900/10 border border-amber-900/30 p-4 rounded-xl grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                    <label className="text-[10px] text-amber-500 uppercase tracking-wider font-bold">Initial Call Status</label>
                    <select 
                        className="w-full bg-[#0c0c0c] border border-amber-900/50 p-3 rounded-lg text-white focus:border-amber-500 outline-none cursor-pointer"
                        value={formData.status}
                        onChange={e => setFormData({...formData, status: e.target.value})}
                    >
                        <option value="NEW">New Lead (Not Called)</option>
                        <option value="CONNECTED">Connected / Talked</option>
                        <option value="DNP">DNP (Did Not Pick)</option>
                        <option value="NOT_REACHABLE">Not Reachable</option>
                        <option value="WRONG_NUMBER">Wrong Number</option>
                        <option value="SITE_VISIT_SCHEDULED">Site Visit Scheduled</option>
                    </select>
                </div>

                <div className="space-y-1">
                    <label className="text-[10px] text-amber-500 uppercase tracking-wider font-bold">Initial Remark / Note</label>
                    <input 
                        className="w-full bg-[#0c0c0c] border border-amber-900/50 p-3 rounded-lg text-white focus:border-amber-500 outline-none" 
                        placeholder="Ex: First call done, interested in modular kitchen"
                        value={formData.remark}
                        onChange={e => setFormData({...formData, remark: e.target.value})} 
                    />
                </div>
            </div>

            <button className="md:col-span-2 bg-amber-600 hover:bg-amber-500 text-black font-bold p-4 rounded-lg mt-2 uppercase tracking-widest shadow-lg transition-all flex justify-center items-center gap-2">
                <Save size={18} /> Save Lead & Log History
            </button>
          </form>
        </div>
      )}

      {/* --- LEAD LIST --- */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-500" size={40}/></div>
      ) : (
        <div className="space-y-6">
          {leads.map((lead) => (
            <div key={lead.id} className="bg-[#161616] border border-zinc-800 rounded-xl overflow-hidden shadow-lg hover:border-zinc-700 transition-all group">
              
              {/* CARD HEADER */}
              <div className="p-6 flex flex-col md:flex-row justify-between items-start gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-xl font-bold text-white group-hover:text-amber-500 transition-colors">{lead.clientName}</h3>
                      
                      {/* Status Badge */}
                      <span className={`text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wider border ${
                          lead.status === 'CONNECTED' ? 'bg-green-950 text-green-400 border-green-900' :
                          lead.status === 'DNP' ? 'bg-red-950 text-red-400 border-red-900' : 
                          lead.status === 'NEW' ? 'bg-blue-950 text-blue-400 border-blue-900' :
                          lead.status === 'SITE_VISIT_SCHEDULED' ? 'bg-purple-950 text-purple-400 border-purple-900' :
                          'bg-zinc-800 text-zinc-400 border-zinc-700'
                      }`}>
                          {lead.status}
                      </span>
                      
                      {/* Source Badge */}
                      <span className="text-[10px] px-2 py-1 rounded font-bold uppercase bg-zinc-900 text-zinc-500 border border-zinc-800">
                          {lead.source === 'MANUAL' ? 'Manual Entry' : 'From Website'}
                      </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-zinc-400 text-sm mt-3">
                     <span className="flex items-center gap-2 hover:text-white transition-colors"><Phone size={14} className="text-amber-600"/> {lead.phone}</span>
                     <span className="flex items-center gap-2 hover:text-white transition-colors"><MapPin size={14} className="text-amber-600"/> {lead.location}</span>
                     {lead.email && <span className="flex items-center gap-2 hover:text-white transition-colors"><Mail size={14} className="text-amber-600"/> {lead.email}</span>}
                  </div>
                </div>

                <button 
                  onClick={() => setExpandedLeadId(expandedLeadId === lead.id ? null : lead.id)}
                  className={`px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all border ${
                      expandedLeadId === lead.id 
                      ? "bg-zinc-800 text-white border-zinc-700" 
                      : "bg-amber-500/5 text-amber-500 border-amber-500/20 hover:bg-amber-500 hover:text-black"
                  }`}
                >
                  {expandedLeadId === lead.id ? "Close Details" : "Manage Lead"}
                </button>
              </div>

              {/* EXPANDED SECTION */}
              {expandedLeadId === lead.id && (
                  <div className="border-t border-zinc-800 bg-[#0c0c0c] p-6 md:p-8 grid md:grid-cols-2 gap-8 animate-in slide-in-from-top-2 duration-300">
                      
                      {/* LEFT: Requirement Edit */}
                      <div>
                          <div className="flex justify-between items-center mb-4">
                              <h4 className="text-amber-500 text-xs uppercase tracking-widest font-bold flex items-center gap-2">
                                <Edit2 size={14}/> Client Requirement
                              </h4>
                              {editingReqId !== lead.id ? (
                                  <button onClick={() => { setEditingReqId(lead.id); setTempReq(lead.requirement); }} className="text-zinc-500 text-xs hover:text-white hover:underline transition-all">
                                      Edit Details
                                  </button>
                              ) : (
                                  <div className="flex gap-3">
                                      <button onClick={() => handleReqUpdate(lead.id)} className="text-green-500 text-xs font-bold hover:underline">Save</button>
                                      <button onClick={() => setEditingReqId(null)} className="text-red-500 text-xs font-bold hover:underline">Cancel</button>
                                  </div>
                              )}
                          </div>

                          {editingReqId === lead.id ? (
                              <textarea 
                                  className="w-full bg-[#161616] border border-zinc-700 p-4 rounded-xl text-sm text-zinc-300 h-40 focus:border-amber-500 outline-none leading-relaxed resize-none"
                                  value={tempReq}
                                  onChange={(e) => setTempReq(e.target.value)}
                              />
                          ) : (
                              <div className="bg-[#161616] border border-zinc-800/50 p-5 rounded-xl min-h-[100px]">
                                  <p className="text-zinc-300 text-sm leading-7">
                                      {lead.requirement || "No requirement details provided yet."}
                                  </p>
                              </div>
                          )}
                      </div>

                      {/* RIGHT: Call History & Actions */}
                      <div className="md:border-l border-zinc-800 md:pl-8">
                          
                          {/* Log New Call */}
                          <div className="bg-zinc-900/40 p-5 rounded-xl mb-6 border border-zinc-800">
                              <h4 className="text-white text-xs font-bold mb-4 uppercase tracking-widest flex items-center gap-2">
                                <Phone size={14} className="text-amber-500"/> Update Call Status
                              </h4>
                              <div className="grid grid-cols-1 gap-3 mb-3">
                                  <select 
                                      className="bg-black border border-zinc-700 text-zinc-300 rounded-lg p-3 text-xs focus:border-amber-500 outline-none cursor-pointer"
                                      onChange={(e) => setLogData({...logData, status: e.target.value})}
                                  >
                                      <option value="DNP">DNP (Did Not Pick)</option>
                                      <option value="CONNECTED">Connected / Talked</option>
                                      <option value="NOT_REACHABLE">Not Reachable</option>
                                      <option value="BUSY">Busy / Cut Call</option>
                                      <option value="WRONG_NUMBER">Wrong Number</option>
                                      <option value="SITE_VISIT_SCHEDULED">Site Visit Scheduled</option>
                                      <option value="CONVERTED">Converted (Deal Closed)</option>
                                  </select>
                                  <input 
                                      className="bg-black border border-zinc-700 text-zinc-300 rounded-lg p-3 text-xs focus:border-amber-500 outline-none"
                                      placeholder="Enter Remark (e.g. Call back at 5PM)"
                                      onChange={(e) => setLogData({...logData, remark: e.target.value})}
                                  />
                              </div>
                              <button 
                                  onClick={() => handleLogSubmit(lead.id)}
                                  className="w-full bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 font-bold py-2.5 rounded-lg text-xs uppercase tracking-widest transition-all"
                              >
                                  Save Status Update
                              </button>
                          </div>

                          {/* History Timeline */}
                          <h4 className="text-zinc-500 text-[10px] uppercase tracking-widest mb-3 flex items-center gap-2">
                            <History size={12}/> Recent Activity Timeline
                          </h4>
                          <div className="max-h-56 overflow-y-auto pr-2 custom-scrollbar space-y-3">
                              {lead.callLogs && lead.callLogs.length > 0 ? (
                                  lead.callLogs.map((log, idx) => (
                                      <div key={idx} className="relative pl-4 border-l-2 border-zinc-800 pb-1 last:pb-0">
                                          {/* Timeline Dot */}
                                          <div className={`absolute -left-[5px] top-1 w-2.5 h-2.5 rounded-full ${
                                              log.status === 'CONNECTED' ? 'bg-green-500' : 
                                              log.status === 'CONVERTED' ? 'bg-amber-500' :
                                              log.status === 'NEW' ? 'bg-blue-500' :
                                              'bg-red-500'
                                          }`}></div>

                                          <div className="bg-zinc-900/30 p-3 rounded-lg border border-zinc-800/50">
                                              <div className="flex justify-between items-start mb-1">
                                                  <span className={`font-bold text-[10px] uppercase tracking-wider ${
                                                      log.status === 'CONNECTED' ? 'text-green-400' : 
                                                      log.status === 'CONVERTED' ? 'text-amber-400' :
                                                      'text-zinc-400'
                                                  }`}>{log.status}</span>
                                                  <span className="text-[9px] text-zinc-600">
                                                      {new Date(log.callTime).toLocaleDateString()} • {new Date(log.callTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                                  </span>
                                              </div>
                                              <p className="text-zinc-300 text-xs">{log.remark}</p>
                                          </div>
                                      </div>
                                  ))
                              ) : (
                                  <p className="text-zinc-600 text-xs italic text-center py-4 border border-dashed border-zinc-800 rounded-lg">
                                    No history available.
                                  </p>
                              )}
                          </div>
                      </div>
                  </div>
              )}
            </div>
          ))}
          
          {!loading && leads.length === 0 && (
              <div className="text-center py-20 bg-[#161616] rounded-xl border border-zinc-800">
                  <p className="text-zinc-500">No leads found. Start by adding a manual lead.</p>
              </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeadManager;